import { createBrowserHistory } from 'history';
import { configureStore as rtkConfigureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { routerMiddleware } from 'connected-react-router';
import createRootReducer from './reducer';
import getSagas from './sagas';

export const history = createBrowserHistory();
const sagaMiddleware = createSagaMiddleware();

export default function configureStore() {
  // RTK's configureStore wires Redux DevTools, sets up combineReducers
  // on the slice map, and turns on `serializableCheck` +
  // `immutableCheck` by default. Both checks fight with this app:
  //   - sagas dispatch saga-effect-shaped actions (non-serializable);
  //   - the in-tree CRR shim's LOCATION_CHANGE payload includes a
  //     `Location` object that's serializable but the dev check on
  //     every reducer dispatch is hot-path overhead;
  //   - the legacy redux-actions reducers occasionally use Date
  //     instances inside payloads (filter timestamps).
  // Disabling both checks matches the legacy `createStore` behaviour
  // exactly. The slice-by-slice migration to RTK `createSlice` (Epic
  // 11 follow-up) re-enables the checks one feature at a time.
  const store = rtkConfigureStore({
    reducer: createRootReducer(history),
    middleware: (getDefault) => getDefault({
      thunk: false,            // sagas, not thunks
      // Re-evaluation post Epic 14i (redux-actions → RTK):
      // serializableCheck was previously firing on Date-in-payload
      // filter timestamps and saga-effect-shaped actions. With the
      // RTK migration these are mostly gone, but a quick experiment
      // is needed to confirm. Keeping disabled until that experiment
      // lands; flip on per-feature once verified.
      serializableCheck: false,
      immutableCheck: false,
    }).concat(routerMiddleware(history), sagaMiddleware),
    devTools: { trace: true, traceLimit: 25 },
  });

  let sagaTask = sagaMiddleware.run(function* () {
    yield getSagas();
  });

  // webpack-era HMR (module.hot + require()) — disabled under
  // Vite. Kept guarded for the (rare) case the file is imported
  // from a Jest/CRA context where `module` exists. Vite's HMR
  // for the store can be added back via import.meta.hot if
  // needed, but it isn't critical for tests.
  if (typeof module !== 'undefined' && module.hot) {
    module.hot.accept('./reducer', () => {
      const nextReducerCreator = require('./reducer').default;
      const nextRootReducer = nextReducerCreator(history);
      store.replaceReducer(nextRootReducer);
    });
    module.hot.accept('./sagas', () => {
      const getNewSagas = require('./sagas').default;
      sagaTask.cancel();
      sagaTask.toPromise().then(() => {
        sagaTask = sagaMiddleware.run(function* replacedSaga() {
          yield getNewSagas();
        });
      });
    });
  }

  return store;
}
